import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { useAccountStore } from '@/stores/accountStore';
import { AuthContext } from './auth-context';
import { onAuthExpired } from '@/lib/authSession';
import { ApiError } from '@/api/core/client';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const setAuthenticated = useAccountStore((state) => state.setAuthenticated);
  const loadAccounts = useAccountStore((state) => state.loadAccounts);

  // Load token from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const savedUsername = localStorage.getItem('authUsername');
    if (savedToken && savedUsername) {
      setToken(savedToken);
      setUsername(savedUsername);
    }
  }, []);

  useEffect(() => {
    const isAuthenticated = Boolean(token);
    setAuthenticated(isAuthenticated, token);
    if (!isAuthenticated) {
      return;
    }
    
    void loadAccounts().catch((error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return;
      }
      console.error('Failed to load accounts after authentication', error);
    });
  }, [loadAccounts, setAuthenticated, token]);

  const login = useCallback((newToken: string, newUsername: string) => {
    setToken(newToken);
    setUsername(newUsername);
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('authUsername', newUsername);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUsername(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUsername');
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthExpired(logout);
    return unsubscribe;
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        token,
        username,
        login,
        logout,
        isAuthenticated: !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
